import { AnyAction, Dispatch } from 'redux';
import api from '../services/api';
import { normalize } from 'normalizr';
import {
  leasePriceMinimized,
  leasePriceDetails,
  generateLeaseKey,
  leaseKeySeparator,
} from './schema';
import { ALL } from '../constants';
import { handleError } from './dashboard';
import { UPLOAD_TO_PMS_ACTIONS } from '.';
import { IUnitLevelItem } from '../components/LeasePricing/ViewUnitLevel/models';

export const IN_LEASE_PRICING_ACTIONS = {
  FETCH_UNIT_DETAILS: 'FETCH_UNIT_DETAILS',
  IS_LOADING: 'ILPA_ISLOADING',
  PRICE_MATRICES_IS_LOADING: 'PM_ISLOADING',
  APPROVE: 'ILPA_APPROVE',
  EXCLUDE: 'ILPA_EXCLUDE',
  APPROVE_UNIT_PRICE: 'APPROVE_UNIT_PRICE',
  EXCLUDE_UNIT_PRICE: 'EXCLUDE_UNIT_PRICE',
  FLAG: 'ILPA_FLAG',
  ADJUST_PRICE: 'ADJUST_PRICE',
  TOGGLE_APPROVE: 'TOGGLE_APPROVE',

  SET_SELECTED_SORT_OPTION: 'SELECTED_SORT_OPTION',
  SET_OFFSET: 'SET_OFFSET',
  SET_LIMIT: 'SET_LIMIT',
  SET_UNIT_LEVEL_PAGINATION_OFFSET: 'SET_UNIT_LEVEL_PAGINATION_OFFSET',
  SET_UNIT_LEVEL_PAGINATION_LIMIT: 'SET_UNIT_LEVEL_PAGINATION_LIMIT',

  SET_SELECTED_UNIT_TYPE: 'ILPA_SELECTED_UNIT_TYPE',
  SET_SELECTED_BEDROOMS: 'ILPA_SET_SELECTED_BEDROOMS',
  SET_SELECTED_PROPERTY: 'ILPA_SELECTED_PROPERTY',

  SET_SELECTED_SHOW_AS: 'SET_SELECTED_SHOW_AS',
  SET_SELECTED_UNIT_KEY: 'SET_SELECTED_UNIT_KEY',
  SET_SEARCH_PHRASE: 'SET_SEARCH_PHRASE',
  SET_SELECTED_OFFER_STATE: 'SET_SELECTED_OFFER_STATE',
  SET_IS_OCCUPIED_STATE: 'SET_IS_OCCUPIED_STATE',
  SET_ALL_UNIT_TYPES: 'SET_ALL_UNIT_TYPES',
  SET_ALL_UNIT_TYPE_DETAILS: 'SET_ALL_UNIT_TYPE_DETAILS',
  SET_PRICE_MATRICES: 'SET_PRICE_MATRICES',
  SET_LEASE_PRICE_SUMMARY: 'SET_LEASE_PRICE_SUMMARY',
  SET_SUMMARY_LENGTH: 'SET_SUMMARY_LENGTH',
  FETCH_OFFER_CYCLES: 'FETCH_OFFER_CYCLES',
  FETCH_UNIT_PRICE_MATRIX: 'FETCH_UNIT_PRICE_MATRIX',
  OFFERS_UPDATE_LOADING: 'OFFERS_UPDATE_LOADING',
  RESET_PRICE_MATRICES: 'RESET_PRICE_MATRICES',
  UPDATE_BEST_OFFER: 'UPDATE_BEST_OFFER',
};

export const NEW_LEASE_ACTIONS = {
  OFFER_CYCLES: 'OFFER_CYCLES',
  SFR_NEW_LEASE_OFFERS: 'SFR_NEW_LEASE_OFFERS',
  UPDATE_BED: 'UPDATE_BED',
  UPDATE_SUBMARKET: 'UPDATE_SUBMARKET',
  UPDATE_SORTBY: 'UPDATE_SORTBY',
  UPDATE_PERIOD: 'UPDATE_PERIOD',
  UPDATE_OFFER_STATUS: 'UPDATE_OFFER_STATUS',
  SFR_PROPERTIES: 'SFR_PROPERTIES',
  OCCUPANCY_REPORTS: 'OCCUPANCY_REPORTS',
  UPDATE_OFFSET: 'UPDATE_OFFSET',
  UPDATE_MARKET: 'UPDATE_MARKET',
  UPDATE_UNITAVAILABILITYSTATUS: 'UPDATE_UNITAVAILABILITYSTATUS',
  SET_SHOW_EDIT_DIALOG: 'SET_SHOW_EDIT_DIALOG',
  UPDATE_UNIT_OFFERS: 'UPDATE_UNIT_OFFERS',
  SET_LEASE_PRICING_MODULE: 'SET_LEASE_PRICING_MODULE',
  UPDATE_SEARCH_UNITID_QUERY: 'UPDATE_SEARCH_UNITID_QUERY',

  //APPROVE ALL
  BULK_APPROVE_UNITLEVEL_PRICE: 'BULK_APPROVE_UNITLEVEL_PRICE',
  BULK_APPROVE_UNITTYPE_PRICE: 'BULK_APPROVE_UNITTYPE_PRICE',
};

export const setLoading = (dispatch: Dispatch, isLoading: boolean) =>
  dispatch({
    type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
    payload: isLoading,
  });

export const getUnitTypesData = () => (dispatch: Dispatch) => {
  setLoading(dispatch, true);
  api
    .getUnitTypes()
    .then((res) => {
      dispatch({
        type: IN_LEASE_PRICING_ACTIONS.SET_ALL_UNIT_TYPES,
        payload: res.data,
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => {
      handleError(err, dispatch);
      console.error(err.message);
    })
    .finally(() => {
      setLoading(dispatch, false);
    });
};

export type LeasePriceAPIParams = {
  beds?: string | number;
  propertyCode?: string | number;
  unitTypeCode?: string | number;
  limit?: string | number;
  offset?: string | number;
  sortBy?: string;
  sortType?: string;
  offerState?: string;
};

export const getLeasePriceData =
  (param: LeasePriceAPIParams) => (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    dispatch({
      type: UPLOAD_TO_PMS_ACTIONS.IS_LOADING,
      payload: { isLoading: true },
    });
    api
      .getUnitTypeDetails(param)
      .then((res2) => {
        const minimizedData = normalize(res2.data.unitTypeData, [
          leasePriceMinimized,
        ]);
        const priceDetails = normalize(res2.data.unitTypeData, [
          leasePriceDetails,
        ]);

        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.SET_ALL_UNIT_TYPE_DETAILS,
          payload: {
            normalized: [minimizedData, priceDetails],
            strategy: res2.data.strategy,
            count: res2.data.count,
          },
          meta: {
            track: true,
          },
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.SET_REFRESH_FUNC_PARAM,
          payload: {
            refreshFuncParam: param,
          },
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.IS_LOADING,
          payload: { isLoading: false },
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.FETCH,
          payload: {
            data: Object.values(
              minimizedData.entities.leasePriceKeys ?? {}
            ).map((leasePriceDetail) => ({
              propertyCode: leasePriceDetail.Property_Code,
              unitType: leasePriceDetail.UnitType,
              uploadOfferUnitType: leasePriceDetail.UploadOfferUnitType,
              status: leasePriceDetail.IsUploaded,
              isApproved: leasePriceDetail.IsApproved,
            })),
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const getUnitDetails =
  (param?: LeasePriceAPIParams) => (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    dispatch({
      type: UPLOAD_TO_PMS_ACTIONS.IS_LOADING,
      payload: { isLoading: true },
    });
    api
      .getUnitDetails(param)
      .then((response: any) => {
        // { data: IUnitLevelItems; pageCount: number }) => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.FETCH_UNIT_DETAILS,
          payload: {
            unitLevelData: response.data.data,
            pageCount: response.pageCount || 10,
          },
          meta: {
            track: true,
          },
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.IS_LOADING,
          payload: { isLoading: false },
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.SET_REFRESH_FUNC_PARAM,
          payload: {
            refreshFuncParam: param,
          },
        });
        dispatch({
          type: UPLOAD_TO_PMS_ACTIONS.FETCH,
          payload: {
            data: (response.data.data ?? []).map(
              (leasePriceDetail: IUnitLevelItem) => ({
                propertyCode: leasePriceDetail.Property_Code,
                unitId: leasePriceDetail.UnitId,
                status: leasePriceDetail.IsUploaded,
                isApproved: leasePriceDetail.IsApproved,
              })
            ),
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
        console.error(err.message);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const setOffSet = (offset: string | number) => (dispatch: Dispatch) =>
  dispatch({
    type: IN_LEASE_PRICING_ACTIONS.SET_OFFSET,
    payload: offset,
    meta: {
      track: true,
    },
  });

export const setLimit = (limit: string | number) => (dispatch: Dispatch) =>
  dispatch({
    type: IN_LEASE_PRICING_ACTIONS.SET_LIMIT,
    payload: limit,
    meta: {
      track: true,
    },
  });

export const setUnitLevelPaginationOffSet =
  (offset: string | number) => (dispatch: Dispatch) =>
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_UNIT_LEVEL_PAGINATION_OFFSET,
      payload: offset,
      meta: {
        track: true,
      },
    });

export const setUnitLevelPaginationLimit =
  (limit: string | number) => (dispatch: Dispatch) =>
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_UNIT_LEVEL_PAGINATION_LIMIT,
      payload: limit,
      meta: {
        track: true,
      },
    });

export const setSelectedSortOption =
  (option?: string) => (dispatch: Dispatch) =>
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_SELECTED_SORT_OPTION,
      payload: option,
      meta: {
        track: true,
      },
    });

export const setSelectedProperty = (property: string) => (dispatch: Dispatch) =>
  dispatch({
    type: IN_LEASE_PRICING_ACTIONS.SET_SELECTED_PROPERTY,
    payload: property,
    meta: {
      track: true,
    },
  });

export const setSearchPhrase = (phrase?: string) => (dispatch: Dispatch) =>
  dispatch({
    type: IN_LEASE_PRICING_ACTIONS.SET_SEARCH_PHRASE,
    payload: phrase,
    meta: {
      track: true,
    },
  });

export const setSelectedBedRooms =
  (bedRooms: string | number = ALL) =>
  (dispatch: Dispatch) =>
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_SELECTED_BEDROOMS,
      payload: bedRooms,
      meta: {
        track: true,
      },
    });

export const setSelectedUnitTypeKey =
  (property_code: string, unit_Type: string) => (dispatch: Dispatch) =>
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_SELECTED_UNIT_KEY,
      payload: generateLeaseKey(property_code, unit_Type),
      meta: {
        track: true,
      },
    });

export const setSelectedUnitType =
  (unit_type_code: string) => (dispatch: Dispatch) =>
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_SELECTED_UNIT_TYPE,
      payload: unit_type_code,
      meta: {
        track: true,
      },
    });

export const setSelectedShowAs =
  (showAsOption: number) => (dispatch: Dispatch) =>
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_SELECTED_SHOW_AS,
      payload: showAsOption,
      meta: {
        track: true,
      },
    });

export const adjustPrice =
  (leaseKey: string, term: number, adjustedPrice: number) =>
  (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    const params = { ...leaseKeySeparator(leaseKey), term, adjustedPrice };
    api
      .adjustLeasePrice(params)
      .then((res) => {
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: res.data.success
              ? 'Price Adjusted'
              : 'Something went wrong',
            type: res.data.success ? 'success' : 'info',
          },
        });
        if (res.data.success)
          dispatch({
            type: IN_LEASE_PRICING_ACTIONS.ADJUST_PRICE,
            payload: { leaseKey, ...params },
            meta: {
              track: true,
            },
          });
      })
      .catch((error) => {
        handleError(error, dispatch);
        console.log('error', error);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const approveUnitPrice =
  (unitDetailId: number, isApprovedFlag: boolean) => (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    api
      .approveUnitLeasePrice({ unitDetailId, isApproved: isApprovedFlag })
      .then((res) => {
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            state: isApprovedFlag,
            message: res.data.success
              ? isApprovedFlag
                ? 'Price Approved'
                : 'Price Un-Approved'
              : 'Something went wrong',
            type: res.data.success ? 'success' : 'info',
          },
        });
        if (res.data.success)
          dispatch({
            type: IN_LEASE_PRICING_ACTIONS.APPROVE_UNIT_PRICE,
            payload: { unitDetailId, isApprovedFlag },
            meta: {
              track: true,
            },
          });
      })
      .catch((err) => {
        console.log(err.message);
        handleError(err, dispatch);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const excludeUnitPrice =
  (unitDetailId: number, isExcludedFlag: boolean) => (dispatch: Dispatch) => {
    setLoading(dispatch, true);

    api
      .excludeUnitLeasePrice({ unitDetailId, isExcluded: isExcludedFlag })
      .then((res) => {
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: res.data.success
              ? isExcludedFlag
                ? 'Price Excluded'
                : 'Price Un-Excluded'
              : 'Something went wrong',
            type: res.data.success ? 'success' : 'info',
          },
        });
        if (res.data.success)
          dispatch({
            type: IN_LEASE_PRICING_ACTIONS.EXCLUDE_UNIT_PRICE,
            payload: { unitDetailId, isExcludedFlag: isExcludedFlag },
            meta: {
              track: true,
            },
          });
      })
      .catch((err) => {
        console.log(err.message);
        handleError(err, dispatch);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const approvePrice =
  (leaseKey: string, currentState: boolean) => (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    const params = leaseKeySeparator(leaseKey);
    api
      .approveLeasePrice(params)
      .then((res) => {
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: res.data.success
              ? !currentState
                ? 'Price Approved'
                : 'Price Un-Approved'
              : 'Something went wrong',
            type: res.data.success ? 'success' : 'info',
          },
        });
        if (res.data.success)
          dispatch({
            type: IN_LEASE_PRICING_ACTIONS.APPROVE,
            payload: { leaseKey, ...params },
            meta: {
              track: true,
            },
          });
      })
      .catch((error) => {
        handleError(error, dispatch);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const excludePrice =
  (leaseKey: string, currentState: boolean) => (dispatch: Dispatch) => {
    setLoading(dispatch, true);
    const params = leaseKeySeparator(leaseKey);
    api
      .excludeLeasePrice(params)
      .then((res) => {
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: res.data.success
              ? !currentState
                ? 'Price Excluded'
                : 'Price Un-Excluded'
              : 'Something went wrong',
            type: res.data.success ? 'success' : 'info',
          },
        });
        if (res.data.success)
          dispatch({
            type: IN_LEASE_PRICING_ACTIONS.EXCLUDE,
            payload: { leaseKey, ...params },
            meta: {
              track: true,
            },
          });
      })
      .catch((error) => {
        handleError(error, dispatch);
      })
      .finally(() => {
        setLoading(dispatch, false);
      });
  };

export const setSummaryLength = (length: number) => (dispatch: Dispatch) => {
  dispatch({
    type: IN_LEASE_PRICING_ACTIONS.SET_SUMMARY_LENGTH,
    payload: {
      length: length,
    },
    meta: {
      track: true,
    },
  });
};

export const getLeasePriceSummary =
  (
    startDate: any,
    length: any,
    bedroom: any,
    propertyCode: string | undefined,
    unitType: string | undefined
  ) =>
  (dispatch: Dispatch) => {
    if (bedroom !== undefined) {
      //setLoading(dispatch, true);
      setLoading(dispatch, true);
      let prevDate = startDate.split('-');
      prevDate[0] = parseInt(prevDate[0]) - 1;
      prevDate = prevDate.join('-');

      Promise.all([
        api.getLeaseSummary({
          startDate: startDate,
          length: length,
          bedRoom: bedroom,
          propertyCode,
          unitType,
        }),
        api.getLeaseSummary({
          startDate: prevDate,
          length: length,
          bedRoom: bedroom,
          propertyCode,
          unitType,
        }),
      ])
        .then(([res1, res2]) => {
          dispatch({
            type: IN_LEASE_PRICING_ACTIONS.SET_LEASE_PRICE_SUMMARY,
            payload: {
              current: res1.data,
              previous: res2.data,
              summaryLength: length,
            },
            meta: {
              track: true,
            },
          });
          //setLoading(dispatch, false);
        })
        .finally(() => {
          setLoading(dispatch, false);
        });
    }
    //setLoading(dispatch, false);
  };

export const setSelectedOfferState =
  (offerState?: string) => (dispatch: Dispatch) => {
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_SELECTED_OFFER_STATE,
      payload: offerState,
      meta: {
        track: true,
      },
    });
  };

export const setIsOccupiedState =
  (isOccupiedState?: string) => (dispatch: Dispatch) => {
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.SET_IS_OCCUPIED_STATE,
      payload: isOccupiedState,
      meta: {
        track: true,
      },
    });
  };

export const getPriceMatrices =
  (selectedUnitTypeKey: string) => (dispatch: Dispatch) => {
    const params = leaseKeySeparator(selectedUnitTypeKey);
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.PRICE_MATRICES_IS_LOADING,
      payload: {
        UnitTypeKey: selectedUnitTypeKey,
        isLoading: true,
      },
    });
    api
      .getPriceMatrices(params)
      .then((res) => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.SET_PRICE_MATRICES,
          payload: {
            priceMatricesData: res.data,
            UnitTypeKey: selectedUnitTypeKey,
          },
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.PRICE_MATRICES_IS_LOADING,
          payload: {
            UnitTypeKey: selectedUnitTypeKey,
            isLoading: false,
          },
        });
      });
  };

export const getUnitPriceMatrices =
  (unitId: number) => (dispatch: Dispatch) => {
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.PRICE_MATRICES_IS_LOADING,
      payload: {
        UnitTypeKey: unitId,
        isLoading: true,
      },
    });
    api
      .getUnitPriceMatrix(unitId)
      .then((res) => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.FETCH_UNIT_PRICE_MATRIX,
          payload: {
            priceMatricesData: res.data,
            UnitTypeKey: unitId,
          },
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => {
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.PRICE_MATRICES_IS_LOADING,
          payload: {
            UnitTypeKey: unitId,
            isLoading: false,
          },
        });
      });
  };

export const getNewLeaseOfferCycles = () => (dispatch: Dispatch) => {
  dispatch({
    type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
    payload: true,
  });
  api
    .getOfferCycles('NewLease')
    .then((res) => {
      console.log(res);

      dispatch({
        type: IN_LEASE_PRICING_ACTIONS.FETCH_OFFER_CYCLES,
        payload: res.data,
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => handleError(err, dispatch))
    .finally(() => {
      dispatch({
        type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
        payload: false,
      });
    });
};

export type SortType = 'ASC' | 'DSC';

type SFRQueryParams = {
  offerCycle?: string;
  beds?: string;
  market?: string;
  limit: number;
  offset: number;
  propertyCode?: string;
  sortBy: string;
  sortType: SortType;
  offerStatus?: string;
  unitAvailabilityStatus?: string;
};
export const getSFRNewLeaseOffers =
  (params: SFRQueryParams) => (dispatch: Dispatch) => {
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
      payload: true,
    });
    api
      .getSFRNewLeaseOffers(params)
      .then((res) => {
        // const normalizedSfrData = norma
        dispatch({
          type: NEW_LEASE_ACTIONS.SFR_NEW_LEASE_OFFERS,
          payload: res.data,
          meta: {
            track: true,
          },
        });
      })
      .catch((err) => handleError(err, dispatch))
      .finally(() => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
          payload: false,
        });
      });
  };

export const updateOfferStatus =
  (offerStatus?: string) => (dispatch: Dispatch) =>
    dispatch({
      type: NEW_LEASE_ACTIONS.UPDATE_OFFER_STATUS,
      payload: offerStatus,
      meta: {
        track: true,
      },
    });

export const updateBed = (bedId: string) => (dispatch: Dispatch) =>
  dispatch({
    type: NEW_LEASE_ACTIONS.UPDATE_BED,
    payload: bedId,
    meta: {
      track: true,
    },
  });
export const updateSubmarket = (submarket: string) => (dispatch: Dispatch) =>
  dispatch({
    type: NEW_LEASE_ACTIONS.UPDATE_SUBMARKET,
    payload: submarket,
    meta: {
      track: true,
    },
  });
export const updateMarket = (market: string) => (dispatch: Dispatch) =>
  dispatch({
    type: NEW_LEASE_ACTIONS.UPDATE_MARKET,
    payload: market,
    meta: {
      track: true,
    },
  });
export const updatePeriod = (period: string) => (dispatch: Dispatch) =>
  dispatch({
    type: NEW_LEASE_ACTIONS.UPDATE_PERIOD,
    payload: period,
    meta: {
      track: true,
    },
  });
export const updateSortBy = (sortby: string) => (dispatch: Dispatch) =>
  dispatch({
    type: NEW_LEASE_ACTIONS.UPDATE_SORTBY,
    payload: sortby,
    meta: {
      track: true,
    },
  });

export const getSFRProperties = () => (dispatch: Dispatch) => {
  return api
    .getSFRProperties()
    .then((res) => {
      let bedSet = new Set();
      let submarkets: any[] = [];
      let beds: any[] = [];
      let markets: any[] = [];
      const marketSet = new Set();
      res.data.data.forEach((elem: any) => {
        if (!bedSet.has(elem.Beds)) {
          bedSet.add(elem.Beds);
          beds.push({
            id: elem.Beds,
            name: elem.Beds,
          });
        }
        if (elem.Market && !marketSet.has(elem.Market)) {
          marketSet.add(elem.Market);
          markets.push({
            id: elem.Market,
            name: elem.Market,
          });
        }
        submarkets.push({
          id: elem.Property_Code,
          name: elem.SubMarket,
        });
      });

      dispatch({
        type: NEW_LEASE_ACTIONS.SFR_PROPERTIES,
        payload: {
          beds: beds.sort((a, b) => a.id - b.id),
          submarkets,
          markets,
          properties: res.data.data,
        },
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => handleError(err, dispatch));
};

export const getOccupancy = (length: number) => (dispatch: Dispatch) => {
  api.occupancyReport(length).then((res) => {
    dispatch({
      type: NEW_LEASE_ACTIONS.OCCUPANCY_REPORTS,
      payload: res.data,
      meta: {
        track: true,
      },
    });
  });
};

export const updateOffset = (offset: number) => (dispatch: Dispatch) => {
  dispatch({
    type: NEW_LEASE_ACTIONS.UPDATE_OFFSET,
    payload: offset,
    meta: {
      track: true,
    },
  });
};
export const updateUnitAvailabilityStatus =
  (status?: string) => (dispatch: Dispatch) => {
    dispatch({
      type: NEW_LEASE_ACTIONS.UPDATE_UNITAVAILABILITYSTATUS,
      payload: status,
      meta: {
        track: true,
      },
    });
  };

export const setShowEditOfferDialogProps =
  (data?: {
    offers: { id: number; Price: number; Term: number }[];
    compareWith?: { title: string; value: number }[];
    unitDetailId: number;
  }) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: NEW_LEASE_ACTIONS.SET_SHOW_EDIT_DIALOG,
      payload: data,
      meta: {
        track: true,
      },
    });
  };

/**
 * Updates the unit price offers for a specific unit price ID.
 *
 * @param {Object} params - The parameters for updating the unit price offers.
 * @param {Array<{id: number, Price: number, Term: number}>} params.offers - The updated offers for the unit price.
 * @param {number} params.unitDetailId - The ID of the unit price to update the offers for.
 * @returns {Function} - A thunk function that dispatches actions to update the unit price offers and show a success or error notification.
 */
export const updateUnitPriceOffers =
  ({
    offersToUpdate,
    offers,
    unitDetailId,
    compareWith,
    shiftAmount,
    shiftBy,
  }: any) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.OFFERS_UPDATE_LOADING,
      payload: true,
    });

    api
      .updateUnitOffers({
        offers: offersToUpdate,
        unitDetailId,
        shiftAmount,
        shiftBy,
      })
      .then((res) => {
        dispatch({
          type: NEW_LEASE_ACTIONS.UPDATE_UNIT_OFFERS,
          payload: { priceMatrices: res.data.unitPriceMatrices, unitDetailId },
          meta: {
            track: true,
          },
        });

        if (!shiftAmount) {
          let newOffers = offers.map((item: any) => {
            let newItem = { ...item };
            if (newItem.UpdatedPrice) {
              newItem.Price = newItem.UpdatedPrice;
            }

            return newItem;
          });
          dispatch({
            type: NEW_LEASE_ACTIONS.SET_SHOW_EDIT_DIALOG,
            payload: {
              compareWith: compareWith,
              offers: newOffers,
              unitDetailId: unitDetailId,
            },
          });
        }

        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: 'Offers updated',
            type: 'success',
          },
        });
      })
      .catch((err) => {
        console.log(err);
        handleError(err, dispatch);
      })
      .finally(() => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.OFFERS_UPDATE_LOADING,
          payload: false,
        });
      });
  };

export const getLeasePricingModule = () => (dispatch: Dispatch) => {
  api
    .getSetting('LeasePricingModule')
    .then((res) => {
      dispatch({
        type: NEW_LEASE_ACTIONS.SET_LEASE_PRICING_MODULE,
        payload: res.data.Value,
        meta: {
          track: true,
        },
      });
    })
    .catch((err) => {
      console.log(err.message);
      handleError(err, dispatch);
      dispatch({
        type: NEW_LEASE_ACTIONS.SET_LEASE_PRICING_MODULE,
        payload: 'UnitType',
      });
    });
};

export const setSearchUnitIdQuery = (query: string) => (dispatch: Dispatch) =>
  dispatch({
    type: NEW_LEASE_ACTIONS.UPDATE_SEARCH_UNITID_QUERY,
    payload: query,
    meta: {
      track: true,
    },
  });

export const shiftPriceMatrice =
  (data: {
    propertyCode: string;
    unitType: string;
    shiftAmount: number;
    shiftBy: string;
  }) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
      payload: true,
    });

    api
      .shiftPriceMatrice({
        propertyCode: data.propertyCode,
        unitType: data.unitType,
        shiftAmount: data.shiftAmount,
        shiftBy: data.shiftBy,
      })
      .then((res) => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
          payload: false,
        });
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.RESET_PRICE_MATRICES,
          payload: data,
          meta: {
            track: true,
          },
        });
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.UPDATE_BEST_OFFER,
          payload: {
            leaseKey: generateLeaseKey(data.propertyCode, data.unitType),
            shiftAmount: data.shiftAmount,
            shiftBy: data.shiftBy,
          },
          meta: {
            track: true,
          },
        });

        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: 'Offers updated',
            type: 'success',
          },
        });
      })
      .catch((err) => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
          payload: false,
        });

        console.log(err);
        handleError(err, dispatch);
      });
  };

export const bulkApproveLeasePricingOffers =
  (data: any) => (dispatch: Dispatch) => {
    dispatch({
      type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
      payload: true,
    });
    const { moduleType, limit, offset, selectedSortOption, ...rest } = data;
    const isUnitType = moduleType === 'UnitType';
    const method = isUnitType
      ? api.bulkApproveUnitTypeOffers
      : api.bulkApproveUnitLevelOffers;
    method(rest)
      .then((res) => {
        dispatch({
          type: isUnitType
            ? NEW_LEASE_ACTIONS.BULK_APPROVE_UNITTYPE_PRICE
            : NEW_LEASE_ACTIONS.BULK_APPROVE_UNITLEVEL_PRICE,
          payload: res.data,
          meta: {
            track: true,
          },
        });
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
          payload: false,
        });
        isUnitType
          ? dispatch(
              getLeasePriceData({
                ...rest,
                limit,
                offset,
                sortBy: selectedSortOption && selectedSortOption.split('_')[0],
                sortType:
                  selectedSortOption && selectedSortOption.split('_')[1],
              }) as unknown as AnyAction
            )
          : dispatch(getUnitDetails(data) as unknown as AnyAction);
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: data.approve ? 'Offers Approved' : 'Offers Un-Approved',
            type: 'success',
          },
        });
      })

      .catch((err) => {
        dispatch({
          type: IN_LEASE_PRICING_ACTIONS.IS_LOADING,
          payload: false,
        });
        dispatch({
          type: 'SHOW_NOTIFICATIONS',
          payload: {
            message: 'Something went wrong',
            type: 'error',
          },
        });

        handleError(err, dispatch);
      });
  };

export const updateUnitListCount = (count: number) => (dispatch: Dispatch) => {
  dispatch({
    type: 'UPDATE_UNIT_LIST_COUNT',
    payload: count,
    meta: {
      track: true,
    },
  });
};
